/** Triage: decide your "To be processed" tasks one card at a time (CEO Tasks' triage, inside ONE). */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Ban, CalendarClock, CalendarDays, Check, ChevronRight, Clock, Inbox, Moon, SkipForward, Sun } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { api, post } from "../lib/api";
import { Card, Empty, ErrorNote, fmtDate, PageHeader } from "../components/ui";

interface TriageItem { id: string; ref: string; title: string; description: string | null; nextAction: string | null; priority: string; dueAt: string | null; createdAt: string; companyName: string | null; source: string | null }
type Action = "today" | "week" | "later" | "someday" | "date" | "done" | "cancel";

const BUTTONS: Array<{ action: Action | "skip"; label: string; key: string; icon: React.ReactNode; tone?: string }> = [
  { action: "today", label: "Do today", key: "1", icon: <Sun size={16} /> },
  { action: "week", label: "This week", key: "2", icon: <CalendarDays size={16} /> },
  { action: "later", label: "Later", key: "3", icon: <Clock size={16} /> },
  { action: "someday", label: "Someday", key: "4", icon: <Moon size={16} /> },
  { action: "done", label: "Already done", key: "5", icon: <Check size={16} />, tone: "text-emerald-700" },
  { action: "cancel", label: "Drop it", key: "6", icon: <Ban size={16} />, tone: "text-red-700" },
  { action: "skip", label: "Skip", key: "s", icon: <SkipForward size={16} /> },
];

export function TriagePage() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["triage"], queryFn: () => api<{ items: TriageItem[]; count: number }>("/api/tasks/triage") });
  const [skipped, setSkipped] = useState<Set<string>>(new Set());
  const [doneIds, setDoneIds] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [date, setDate] = useState("");
  const [tally, setTally] = useState(0);

  const queue = useMemo(() => (q.data?.items ?? []).filter((i) => !doneIds.has(i.id)), [q.data, doneIds]);
  const current = queue.find((i) => !skipped.has(i.id)) ?? null;
  const left = queue.length;

  const act = async (a: Action | "skip") => {
    if (!current || busy) return;
    setError(null);
    if (a === "skip") {
      setSkipped((s) => new Set(s).add(current.id));
      return;
    }
    if (a === "date" && !date) {
      setError(new Error("Pick a date first"));
      return;
    }
    setBusy(true);
    try {
      await post(`/api/tasks/${current.id}/triage`, { action: a, date: a === "date" ? date : undefined });
      setDoneIds((s) => new Set(s).add(current.id));
      setTally((n) => n + 1);
      setDate("");
      void qc.invalidateQueries({ queryKey: ["my-day"] });
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement || e.metaKey || e.ctrlKey) return;
      const b = BUTTONS.find((x) => x.key === e.key.toLowerCase());
      if (b) {
        e.preventDefault();
        void act(b.action);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  return (
    <div className="mx-auto max-w-2xl space-y-5">
      <PageHeader
        eyebrow="Tasks"
        title="Triage"
        subtitle={left ? `${left} to decide${tally ? ` · ${tally} done this session` : ""}. Decide each one in a second — nothing is lost.` : undefined}
        actions={<Link to="/work" className="btn btn-sm">All tasks <ChevronRight size={14} /></Link>}
      />
      <ErrorNote error={q.error ?? error} />
      {q.isLoading ? <Empty>Loading…</Empty> : !current ? (
        <Card>
          <Empty icon={<Inbox size={20} />}>
            {left ? `You skipped the remaining ${left}. Reload to go through them again.` : tally ? `All clear — ${tally} decided. Nice.` : "Nothing waiting to be processed."}
          </Empty>
        </Card>
      ) : (
        <Card>
          <div className="space-y-4">
            <div>
              <div className="muted flex flex-wrap items-center gap-2 text-xs">
                <span className="num">{current.ref}</span>
                {current.companyName && <span className="chip">{current.companyName}</span>}
                {current.priority === "P1" ? <span className="chip text-red-700">Urgent</span> : current.priority === "P2" ? <span className="chip">High</span> : null}
                <span>added {fmtDate(current.createdAt)}</span>
              </div>
              <Link to="/work/$id" params={{ id: current.id }} className="mt-2 block text-lg font-semibold hover:underline">{current.title}</Link>
              {current.nextAction && <p className="mt-1 text-sm"><span className="muted">Next: </span>{current.nextAction}</p>}
              {current.description && <p className="muted mt-2 line-clamp-4 whitespace-pre-wrap text-sm">{current.description.replace(/<[^>]+>/g, " ")}</p>}
            </div>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              {BUTTONS.map((b) => (
                <button key={b.action} className={`btn justify-start ${b.tone ?? ""}`} disabled={busy} onClick={() => void act(b.action)}>
                  {b.icon} {b.label} <kbd className="muted ml-auto text-[11px]">{b.key.toUpperCase()}</kbd>
                </button>
              ))}
              <span className="col-span-2 flex items-center gap-2 sm:col-span-3">
                <CalendarClock size={16} className="muted" />
                <input type="date" className="input !w-44" value={date} onChange={(e) => setDate(e.target.value)} aria-label="Pick a date" />
                <button className="btn" disabled={busy || !date} onClick={() => void act("date")}>Set date</button>
              </span>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}
